/* eslint-disable max-len */
import React, { FunctionComponent } from "react";
import { Helmet } from "react-helmet";
import builder from "content-security-policy-builder";
import { getSiteMetadata } from "../../graphQl/queries/getSiteMetadata";

interface IMetaProps {
  canonicalPathname?: string;
  title?: string;
  metaTitle?: string;
  description?: string;
  image?: string;
  pathname?: string;
  titleTemplate?: string;
  jsonLd?: object; // eslint-disable-line @typescript-eslint/ban-types
  noIndex?: boolean;
  includeAggregateRating?: boolean;
}

const cdn = `02.cdn.${DOMAIN}`;
const csp = () => {
  const connectSrcEnvConfig = Array.isArray(CONTENT_SECURITY_POLICY_CONNECT_SRC)
    ? CONTENT_SECURITY_POLICY_CONNECT_SRC.join(" ")
    : "";
  const scriptSrcEnvConfig = Array.isArray(CONTENT_SECURITY_POLICY_SCRIPT_SRC)
    ? CONTENT_SECURITY_POLICY_SCRIPT_SRC.join(" ")
    : "";

  return builder({
    directives: {
      defaultSrc: ["'self'", "02.cdn.trussle.com", cdn],
      styleSrc: [
        "'self'",
        "'unsafe-inline'",
        "*.cloudflare.com",
        "*.cloudfront.net",
        "fonts.googleapis.com",
        "tagmanager.google.com",
        "02.cdn.trussle.com",
        cdn,
      ],
      scriptSrc: [
        "'self'",
        "'unsafe-inline'",
        "'unsafe-eval'",
        "cdn-assets-prod.s3.amazonaws.com",
        "optimizely.s3.amazonaws.com",
        "bat.bing.com",
        "*.cloudfront.net",
        "cookie-cdn.cookiepro.com",
        "stats.g.doubleclick.net",
        "fast.ssqt.io",
        "edge.fullstory.com",
        "fullstory.com",
        "rs.fullstory.com",
        "googleads.g.doubleclick.net",
        "connect.facebook.net",
        "www.facebook.com",
        "graph.facebook.com",
        "*.googleadservices.com",
        "www.google-analytics.com",
        "optimize.google.com",
        "tagmanager.google.com",
        "*.googleapis.com",
        "www.googletagmanager.com",
        "static.hotjar.com",
        "script.hotjar.com",
        "*.hotjar.io",
        "js.hs-analytics.net",
        "js.hs-scripts.com",
        "js.hsforms.net",
        "forms.hsforms.com",
        "js-na1.hs-scripts.com",
        "js.hs-banner.com",
        "js.hubspot.com",
        "js.usemessages.com",
        "e.infogram.com",
        "infogram.com",
        "*.optimizely.com",
        "cdn.segment.com",
        "snapengage.com",
        "survey.survicate.com",
        "surveys-static.survicate.com",
        "*.trussle.com",
        `*.${DOMAIN}`,
        "widget.trustpilot.com",
        "cdn.taboola.com",
        "trc.taboola.com",
        "amplify.outbrain.com",
        "tr.outbrain.com",
        "wave.outbrain.com",
        "jscloud.net",
        "redditstatic.com",
        "pixel-config.reddit.com",
        "conversions-config.reddit.com",
        scriptSrcEnvConfig,
      ],
      frameSrc: [
        "12820922.fls.doubleclick.net",
        "td.doubleclick.net",
        "connect.facebook.com",
        "www.facebook.com",
        "vars.hotjar.com",
        "forms.hsforms.com",
        "app.hubspot.com",
        "e.infogram.com",
        "infogram.com",
        "*.cdn.optimizely.com",
        "snapengage.com",
        "trussle.eu.looker.com",
        "widget.trustpilot.com",
        "www.youtube.com",
      ],
      fontSrc: [
        "data:",
        "'self'",
        "fonts.gstatic.com",
        "stats.gstatic.com",
        "script.hotjar",
        "02.cdn.trussle.com",
        "trussle.com",
        cdn,
        DOMAIN,
        "surveys-static.survicate.com",
      ],
      connectSrc: [
        "'self'",
        "airbrake.io",
        "bat.bing.com",
        "cookie-cdn.cookiepro.com",
        "geolocation.onetrust.com",
        "stats.g.doubleclick.net",
        "googleads.g.doubleclick.net",
        "facebook.com",
        "www.facebook.com",
        "connect.facebook.com",
        "graph.facebook.com",
        "fullstory.com",
        "edge.fullstory.com",
        "rs.fullstory.com",
        "*.google-analytics.com",
        "*.analytics.google.com",
        "adservice.google.com",
        "googletagmanager.com",
        "www.googletagmanager.com",
        "google.com",
        "www.google.com",
        "pagead2.googlesyndication.com",
        "*.hotjar.com",
        "wss://*.hotjar.com",
        "vc.hotjar.io:*",
        "forms.hsforms.com",
        "api.hubspot.com",
        "hubspot-forms-static-embed.s3.amazonaws.com",
        "app.referralsaasquatch.com",
        "fast.ssqt.io",
        "cdn.segment.io",
        "cdn.segment.com",
        "api.segment.io",
        "snapengage.com",
        "respondent.survicate.com",
        "*.optimizely.com",
        "trc.taboola.com",
        "trc-events.taboola.com",
        "02.cdn.trussle.com",
        "amplify.outbrain.com",
        "tr.outbrain.com",
        "wave.outbrain.com",
        cdn,
        "track-api.trussle.com",
        "track-cdn.trussle.com",
        "jscloud.net",
        "cta-service-cms2.hubspot.com",
        `track-api.${DOMAIN}`,
        `track-cdn.${DOMAIN}`,
        "redditstatic.com",
        "pixel-config.reddit.com",
        "conversions-config.reddit.com",
        connectSrcEnvConfig,
      ],
      imgSrc: [
        "'self'",
        "data:",
        "bat.bing.com",
        "*.cloudfront.net",
        "res.cloudinary.com",
        "cookie-cdn.cookiepro.com",
        "images.ctfassets.net",
        "doubleclick.net",
        "googleads.g.doubleclick.net",
        "facebook.com",
        "www.facebook.com",
        "connect.facebook.com",
        "scontent.ffab1-2.fna.fbcdn.net",
        "google.com",
        "www.google.com",
        "google.co.uk",
        "www.google.co.uk",
        "google-analytics.com",
        "www.google-analytics.com",
        "www.googleadservices.com",
        "www.googleapis.com",
        "googletagmanager.com",
        "www.googletagmanager.com",
        "www.googleusercontent.com",
        "stats.gstatic.com",
        "script.hotjar.com",
        "hubspot.com",
        "forms.hsforms.com",
        "forms-na1.hsforms.com",
        "track.hubspot.com",
        "snapengage.com",
        "surveys-static.survicate.com",
        "02.cdn.trussle.com",
        cdn,
        "i.ytimg.com",
        "emailsignature.trustpilot.com",
        "tr.outbrain.com",
        "perf-na1.hsforms.com",
        "alb.reddit.com",
      ],
      mediaSrc: ["snapengage.com"],
      objectSrc: ["snapengage.com"],
    },
  });
};

const removeAggregaeRating = (jsonLd: any) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { aggregateRating, ...newJsonLd } = jsonLd;
  return newJsonLd;
};
/**
 * Convert legacy format to the new format ("| Trussle" is appended at the end by the title template)
 * "Great mortage deals | Trussle" => "Great mortgage deals"
 * @param title
 */
const formatLegacyTitles = (title = "") => title.replace(/ \| Trussle$/, "");

const Meta: FunctionComponent<IMetaProps> = (props) => {
  const {
    canonicalPathname,
    title,
    metaTitle,
    description,
    image,
    pathname,
    titleTemplate,
    jsonLd,
    noIndex,
    includeAggregateRating,
  } = props;

  const {
    defaultTitle,
    defaultTitleTemplate,
    siteName,
    siteUrl,
    defaultImage,
    twitterUsername,
    defaultMetaDescription,
    defaultJsonLd,
  } = getSiteMetadata();
  const convertedDefaultJsonLd = defaultJsonLd
    ? convertSymbols(defaultJsonLd)
    : "";

  const url = pathname !== null && pathname !== undefined
    ? `${siteUrl}${pathname}`
    : undefined;
  const seo = {
    canonicalUrl:
      canonicalPathname !== null && canonicalPathname !== undefined
        ? `${siteUrl}${canonicalPathname}`
        : url,
    description: description || defaultMetaDescription,
    image: `${image || `${siteUrl}${defaultImage}`}`,
    metaTitle: metaTitle || title || defaultTitle,
    title: title || defaultTitle,
    url,
  };
  const newJsonLd = includeAggregateRating ? (jsonLd || convertedDefaultJsonLd) : removeAggregaeRating(jsonLd || convertedDefaultJsonLd);
  return (
    <Helmet
      htmlAttributes={{ lang: "en" }}
      title={formatLegacyTitles(seo.title)}
      titleTemplate={titleTemplate || defaultTitleTemplate}
    >
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {seo.canonicalUrl && <link rel="canonical" href={seo.canonicalUrl} />}
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      {/* Facebook */}
      {url && <meta property="og:url" content={seo.url} />}
      <meta property="og:title" content={seo.metaTitle || seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:site_name" content={siteName} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={twitterUsername} />
      <meta name="twitter:title" content={seo.metaTitle || seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <script type="application/ld+json">
        {JSON.stringify(newJsonLd)}
      </script>
      <meta name="csp" httpEquiv="Content-Security-Policy" content={csp()} />
    </Helmet>
  );
};

export { Meta, convertSymbols };

const convertSymbols = (data: any) => {
  const objectKeys = Object.keys(data);

  const transformedObject = objectKeys.reduce((acc: any, key: string) => {
    const newKey = key.replace("_", "@");

    if (typeof data[key] === "object") {
      acc[newKey] = convertSymbols(data[key]);
      return acc;
    }

    acc[newKey] = data[key];
    return acc;
  }, {});

  return transformedObject;
};
